import { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import gtmService from '../../../../utils/gtm';
import { useRootDispatch, useRootSelector } from '../../../../store/hooks';
import { selectOptions } from '../../../../store/features/options';
import { selectPageConfig } from '../../../../store/features/pageConfig';
import { selectSiteHeader } from '../../../../store/features/siteHeader';
import { fetchLeadGenFormAction } from '../../../../store/commonDucks/leadGenForm';
import { fetchDownloadableAgendaAction } from '../../../../store/siteDucks/siteDownloadableAgenda';
import { IdentityProviders } from '../../../../constants';
import { getPingIdentityAuthUrl } from '../../../../helpers/authorization/getPingIdentityAuthUrl';
import { getAuthorizationParamsFromCookies } from '../../../../helpers/authorization/getAuthorizationParamsFromCookies';
import { selectLocationConfig } from '../../../../store/features/locationConfig';
import {
  selectLeadGenForm,
  selectLeadGenFormLoaded,
} from '../../../../store/features/leadGen';
import SiteLayout from '../../../layouts/SiteLayout/SiteLayout';
import CallToAction from '../../../shared/CallToAction/CallToAction';
import LeadGen from './LeadGen';
import Layout from './LeadGen/Layout';

/* istanbul ignore next */
export default function DownloadableAgenda() {
  const CONTENT_TYPE = 'agenda';

  const leadGenLoaded = useRootSelector(selectLeadGenFormLoaded);
  const [showLeadGen, setShowLeadGen] = useState(leadGenLoaded);
  const [showAgenda, setShowAgenda] = useState(false);

  const [cookies] = useCookies(['access_token', 'ping-token']);

  const dispatch = useRootDispatch();

  const { siteId, siteType } = useRootSelector(selectPageConfig);
  const { origin, pathname } = useRootSelector(selectLocationConfig);
  const { productCode, pingAuthIdentityEnabled } =
    useRootSelector(selectOptions);
  const { gateId, form: { type } = {} } = useRootSelector(selectLeadGenForm);
  const {
    meta: { title },
  } = useRootSelector(selectSiteHeader);

  useEffect(() => {
    gtmService.pushContentType(CONTENT_TYPE, 'Set Agenda content type');
  }, []);

  useEffect(() => {
    if (type) {
      gtmService.pushFormType(type, 'Set Agenda content type');
    }
  }, [type]);

  useEffect(() => {
    function fetchForm() {
      const authorizationParams = getAuthorizationParamsFromCookies({
        siteType,
        pingAuthIdentityEnabled,
        getTokenFormCookie: (tokenCookieName) => cookies[tokenCookieName],
      });
      const queryParams = {
        ...authorizationParams,
        product_code: productCode,
      };

      const params = {
        leadGenType: CONTENT_TYPE,
        leadGenId: siteId,
        queryParams,
      };

      dispatch(
        fetchLeadGenFormAction(params, (response) => {
          if (response.statusCode === 200) {
            dispatch(fetchDownloadableAgendaAction()).then(() => {
              setShowAgenda(true);
            });
          }

          if (response.statusCode === 403) {
            if (
              authorizationParams.identity === IdentityProviders.Ping &&
              response.form?.type === 'REGISTRATION'
            ) {
              const authRedirectUrl = getPingIdentityAuthUrl({
                origin,
                pathname,
              });

              window.location.assign(authRedirectUrl);
            } else {
              setShowLeadGen(true);
            }
          }
        }),
      );
    }

    fetchForm();
  }, [
    dispatch,
    productCode,
    siteId,
    siteType,
    pingAuthIdentityEnabled,
    origin,
    pathname,
    cookies,
  ]);

  const agendaUrl = gateId ? '/downloads/agenda/gated' : '/downloads/agenda';

  const handleOnClick = () => {
    gtmService.ga4DownloadClick({
      cta_title: 'Download Now',
      agenda_link: agendaUrl,
    });

    gtmService.ga4GenerateLead({
      event_label: `User downloaded agenda`,
      form_id: 'download_agenda_form_id',
      form_title: `Download Now`,
    });
  };

  return (
    <SiteLayout
      helmet={{
        title: title ? `Agenda Download | ${title}` : 'Agenda Download',
      }}
      style={{ backgroundColor: '#fff' }}
    >
      {showAgenda && (
        <Layout>
          <CallToAction to={agendaUrl} sitePath onClick={handleOnClick}>
            Download Now
          </CallToAction>
        </Layout>
      )}
      {showLeadGen && <LeadGen />}
    </SiteLayout>
  );
}
